import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft,
    ArrowRight,
    Clock,
    Wallet,
    MapPin,
    Users,
    CalendarDays,
    Target,
    BookOpen,
    GraduationCap,
    ClipboardCheck,
    CheckCircle2,
} from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/ScrollReveal";
import FormateurPublicCard from "@/components/formateur/FormateurPublicCard";

function formatDate(dateIso) {
    if (!dateIso) return "";
    const d = new Date(dateIso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

export default function FormationDetail() {
    const { slug } = useParams();
    const [formation, setFormation] = useState(null);
    const [formateurs, setFormateurs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setNotFound(false);
        setFormateurs([]);
        api.get(`/formations/${slug}`)
            .then((r) => alive && setFormation(r.data))
            .catch(() => alive && setNotFound(true))
            .finally(() => alive && setLoading(false));
        api.get(`/formations/${slug}/formateurs`)
            .then((r) => alive && setFormateurs(Array.isArray(r.data) ? r.data : []))
            .catch(() => alive && setFormateurs([]));
        return () => { alive = false; };
    }, [slug]);

    if (loading) {
        return (
            <div className="pt-40 pb-24 text-center" data-testid="formation-loading">
                <span className="font-display text-xl text-brand-smoke">Chargement…</span>
            </div>
        );
    }

    if (notFound || !formation) {
        return (
            <div className="pt-40 pb-24 text-center" data-testid="formation-not-found">
                <h1 className="font-display text-4xl">Formation introuvable</h1>
                <Link to="/expertise-pa" className="mt-6 inline-flex items-center gap-2 text-brand-orange font-semibold">
                    <ArrowLeft className="h-4 w-4" /> Retour aux cursus
                </Link>
            </div>
        );
    }

    const { cursus } = formation;

    return (
        <div data-testid="page-formation-detail">
            {/* Hero */}
            <section className="relative bg-brand-ink pt-40 pb-20 text-white md:pt-48 md:pb-28 grain">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal>
                        <Link
                            to={`/cursus/${formation.cursus_slug}`}
                            data-testid="back-to-cursus"
                            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange hover:text-white"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" />
                            {cursus?.name || "Retour"}
                        </Link>
                        <h1 className="mt-8 max-w-4xl font-display text-5xl font-normal leading-[1.02] tracking-tightest sm:text-6xl lg:text-7xl">
                            {formation.name}
                        </h1>
                        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/75">
                            {formation.short_description}
                        </p>

                        <div className="mt-10 flex flex-wrap items-center gap-3">
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm text-white/85 backdrop-blur">
                                <Clock className="h-4 w-4 text-brand-orange" /> {formation.duration_hours}h — 1 journée
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm text-white/85 backdrop-blur">
                                <Wallet className="h-4 w-4 text-brand-orange" /> {formation.price_eur}€
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm text-white/85 backdrop-blur">
                                <Users className="h-4 w-4 text-brand-orange" /> 12 participants max.
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm text-white/85 backdrop-blur">
                                Présentiel
                            </span>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Content + Sidebar */}
            <section className="bg-white py-20 md:py-28">
                <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <article className="lg:col-span-8 space-y-16">
                        <Reveal>
                            <div>
                                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">Présentation</span>
                                <h2 className="mt-4 font-display text-3xl sm:text-4xl leading-tight">Une formation conçue pour durer.</h2>
                                <p className="mt-6 text-lg leading-relaxed text-brand-ink/80">
                                    {formation.long_description}
                                </p>
                            </div>
                        </Reveal>

                        {/* Objectifs */}
                        <Reveal>
                            <div className="rounded-3xl border border-brand-line bg-brand-sand/60 p-8 md:p-10 shadow-premium">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                                        <Target className="h-5 w-5" />
                                    </div>
                                    <h3 className="font-display text-2xl">Objectifs pédagogiques</h3>
                                </div>
                                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                                    {formation.objectives.map((obj) => (
                                        <li key={obj} className="flex items-start gap-3">
                                            <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-orange" />
                                            <span className="text-sm leading-relaxed text-brand-ink/85">{obj}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>

                        {/* Programme */}
                        <Reveal>
                            <div>
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                                        <BookOpen className="h-5 w-5" />
                                    </div>
                                    <h3 className="font-display text-2xl">Programme détaillé</h3>
                                </div>
                                <div className="mt-8 space-y-4">
                                    {formation.program_modules.map((m, idx) => (
                                        <div
                                            key={m.title}
                                            data-testid={`program-module-${idx}`}
                                            className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium"
                                        >
                                            <div className="flex flex-wrap items-start justify-between gap-4">
                                                <div>
                                                    <div className="font-display text-xs uppercase tracking-[0.2em] text-brand-smoke">
                                                        Module 0{idx + 1}
                                                    </div>
                                                    <h4 className="mt-2 font-display text-xl text-brand-ink">{m.title}</h4>
                                                </div>
                                                <span className="rounded-full bg-brand-orange/10 px-3 py-1 text-xs font-semibold text-brand-orange">
                                                    {m.duration}
                                                </span>
                                            </div>
                                            <p className="mt-4 text-sm leading-relaxed text-brand-ink/75">{m.content}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Reveal>

                        {/* Votre formateur */}
                        {formateurs.length > 0 && (
                            <Reveal>
                                <div data-testid="formation-formateur-section">
                                    <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-brand-orange">
                                        Votre formateur
                                    </span>
                                    <h3 className="mt-4 font-display text-3xl leading-tight sm:text-4xl lg:text-5xl">
                                        {formateurs.length === 1
                                            ? <>Votre <em className="italic text-brand-smoke">formateur.</em></>
                                            : <>Vos <em className="italic text-brand-smoke">formateurs.</em></>
                                        }
                                    </h3>
                                    <p className="mt-4 max-w-xl text-sm text-brand-ink/70 md:text-base">
                                        Un praticien expert, à la pointe de sa discipline.
                                    </p>
                                    <div className="mt-8">
                                        {formateurs.length === 1 ? (
                                            <FormateurPublicCard formateur={formateurs[0]} variant="horizontal" />
                                        ) : (
                                            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                                                {formateurs.map((f, i) => (
                                                    <FormateurPublicCard key={f.id || f.full_name} formateur={f} index={i} variant="compact" />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Reveal>
                        )}

                        {/* Méthodes */}
                        <Reveal>
                            <div className="grid gap-8 md:grid-cols-2">
                                <div className="rounded-3xl border border-brand-line bg-white p-8 shadow-premium">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                                            <GraduationCap className="h-5 w-5" />
                                        </div>
                                        <h3 className="font-display text-xl">Méthodes pédagogiques</h3>
                                    </div>
                                    <ul className="mt-5 space-y-2.5">
                                        {formation.methods.map((m) => (
                                            <li key={m} className="flex items-start gap-2.5 text-sm text-brand-ink/80">
                                                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-orange" />
                                                {m}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="rounded-3xl border border-brand-line bg-white p-8 shadow-premium">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                                            <ClipboardCheck className="h-5 w-5" />
                                        </div>
                                        <h3 className="font-display text-xl">Modalités d'évaluation</h3>
                                    </div>
                                    <ul className="mt-5 space-y-2.5">
                                        {formation.evaluation_methods.map((m) => (
                                            <li key={m} className="flex items-start gap-2.5 text-sm text-brand-ink/80">
                                                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-orange" />
                                                {m}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </Reveal>

                        {/* Public cible */}
                        <Reveal>
                            <div className="rounded-3xl bg-brand-ink p-10 text-white">
                                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">
                                    Public cible
                                </div>
                                <h3 className="mt-3 font-display text-2xl">{formation.target_audience}</h3>
                                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/70">
                                    La formation est conçue pour les kinésithérapeutes diplômés d'État exerçant en libéral ou en structure.
                                    Aucune pré-requis technique spécifique n'est exigé, seul un solide socle clinique est demandé.
                                </p>
                            </div>
                        </Reveal>

                        {/* Sessions */}
                        <Reveal>
                            <div id="sessions">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                                        <CalendarDays className="h-5 w-5" />
                                    </div>
                                    <h3 className="font-display text-2xl">Sessions disponibles</h3>
                                </div>
                                {formation.sessions && formation.sessions.length > 0 ? (
                                    <StaggerGroup className="mt-8 space-y-4">
                                        {formation.sessions.map((s) => (
                                            <StaggerItem key={s.id}>
                                                <div
                                                    data-testid={`formation-session-${s.id}`}
                                                    className="flex flex-col gap-6 rounded-2xl border border-brand-line bg-white p-6 shadow-premium md:flex-row md:items-center md:justify-between"
                                                >
                                                    <div className="flex flex-wrap items-center gap-5">
                                                        <div>
                                                            <div className="text-xs font-semibold uppercase tracking-wider text-brand-orange">
                                                                {formatDate(s.start_date)}
                                                            </div>
                                                            <div className="mt-1 text-sm font-medium text-brand-ink">
                                                                {s.city} · {s.venue_name}
                                                            </div>
                                                        </div>
                                                        <div className="hidden h-10 w-px bg-brand-line md:block" />
                                                        <div>
                                                            <div className="text-xs uppercase tracking-wider text-brand-smoke">Formateur</div>
                                                            <div className="mt-1 text-sm font-medium text-brand-ink">{s.trainer_name}</div>
                                                        </div>
                                                        <div className="hidden h-10 w-px bg-brand-line md:block" />
                                                        <div>
                                                            <div className="text-xs uppercase tracking-wider text-brand-smoke">Places</div>
                                                            <div className="mt-1 text-sm font-medium text-brand-ink">{s.available_seats} restantes</div>
                                                        </div>
                                                    </div>
                                                    <Link
                                                        to={`/inscription/${s.id}`}
                                                        data-testid={`session-inscription-${s.id}`}
                                                        className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#d9772c]"
                                                    >
                                                        S'inscrire
                                                        <ArrowRight className="h-4 w-4" />
                                                    </Link>
                                                </div>
                                            </StaggerItem>
                                        ))}
                                    </StaggerGroup>
                                ) : (
                                    <p className="mt-6 text-base text-brand-ink/70">
                                        Aucune session n'est programmée pour l'instant.{" "}
                                        <Link to="/contact" className="text-brand-orange font-semibold hover:underline">
                                            Contactez-nous
                                        </Link>{" "}
                                        pour être tenu informé.
                                    </p>
                                )}
                            </div>
                        </Reveal>
                    </article>

                    {/* Sidebar sticky */}
                    <aside className="lg:col-span-4">
                        <div className="lg:sticky lg:top-28">
                            <div className="rounded-3xl border border-brand-line bg-white p-8 shadow-premium-lg" data-testid="formation-sidebar">
                                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">
                                    En résumé
                                </div>
                                <h3 className="mt-3 font-display text-2xl leading-tight">{formation.name}</h3>

                                <dl className="mt-6 space-y-4 text-sm">
                                    <div className="flex items-center justify-between border-b border-brand-line pb-4">
                                        <dt className="text-brand-smoke">Durée</dt>
                                        <dd className="font-semibold text-brand-ink">{formation.duration_hours}h · 1 jour</dd>
                                    </div>
                                    <div className="flex items-center justify-between border-b border-brand-line pb-4">
                                        <dt className="text-brand-smoke">Prix</dt>
                                        <dd className="font-display text-2xl text-brand-orange">{formation.price_eur} €</dd>
                                    </div>
                                    <div className="flex items-center justify-between border-b border-brand-line pb-4">
                                        <dt className="text-brand-smoke">Format</dt>
                                        <dd className="font-semibold text-brand-ink">Présentiel</dd>
                                    </div>
                                    <div className="flex items-center justify-between border-b border-brand-line pb-4">
                                        <dt className="text-brand-smoke">Effectif</dt>
                                        <dd className="font-semibold text-brand-ink">12 max.</dd>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <dt className="text-brand-smoke">Prise en charge</dt>
                                        <dd className="font-semibold text-brand-ink">FIF PL · DPC</dd>
                                    </div>
                                </dl>

                                {formation.sessions && formation.sessions.length > 0 ? (
                                    <Link
                                        to={`/inscription/${formation.sessions[0].id}`}
                                        data-testid="sidebar-cta-inscription"
                                        className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#d9772c]"
                                    >
                                        S'inscrire à la prochaine session
                                        <ArrowRight className="h-4 w-4" />
                                    </Link>
                                ) : (
                                    <Link
                                        to="/contact"
                                        data-testid="sidebar-cta-contact"
                                        className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#d9772c]"
                                    >
                                        Me tenir informé
                                    </Link>
                                )}
                                <Link
                                    to="/prise-en-charge"
                                    data-testid="sidebar-cta-prise-en-charge"
                                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-brand-line px-6 py-3.5 text-sm font-semibold text-brand-ink transition-all hover:border-brand-orange hover:text-brand-orange"
                                >
                                    Voir la prise en charge
                                </Link>
                            </div>

                            <div className="mt-6 rounded-3xl border border-brand-line bg-brand-sand p-6 text-sm text-brand-ink/75 shadow-premium">
                                <MapPin className="h-5 w-5 text-brand-orange" />
                                <p className="mt-3 leading-relaxed">
                                    Sessions organisées à <strong>Paris, Lyon, Marseille et Bordeaux</strong>. Tous nos lieux
                                    sont pensés pour des conditions d'apprentissage optimales (luminosité, matériel, espace pratique).
                                </p>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}

